import { brand } from '@alison/ui';
import type { Theme } from '@mui/material/styles';
import { lighten, useTheme } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import type { ReactNode } from 'react';

export const TREND_CHART_SERIES_COLORS = [
  lighten(brand.blue, 0.5),
  lighten(brand.purple, 0.3),
] as const;

const AREA_GRADIENT_ID = 'trendChartArea';

interface TrendChartSeries {
  id: string;
  label: string;
  values: (number | null)[];
  valueFormatter: (value: number) => string;
  color?: string;
  dashed?: boolean;
  yAxisId?: string;
}

interface TrendChartProps {
  xAxisData: (string | number)[];
  xAxisTickFormatter: (value: string | number) => string;
  xAxisValueFormatter?: (value: string | number) => string;
  series: TrendChartSeries[];
}

export function trendChartSx(theme: Theme) {
  return {
    '& .MuiLineChart-area': { fill: `url(#${AREA_GRADIENT_ID})` },
    '& .MuiLineChart-line, & .MuiLineElement-root': { strokeWidth: 2 },
    '& .MuiChartsAxis-tickLabel': {
      fill: theme.palette.text.disabled,
      fontSize: 10,
      fontWeight: 600,
    },
    '& .MuiChartsAxisHighlight-root': { stroke: theme.palette.divider },
    '& .MuiChartsGrid-line': { stroke: theme.palette.divider, strokeOpacity: 0.5 },
  };
}

export function TrendChartAreaGradient(): ReactNode {
  return (
    <defs>
      <linearGradient id={AREA_GRADIENT_ID} x1="0" x2="0" y1="0" y2="1">
        <stop offset="-14%" stopColor={TREND_CHART_SERIES_COLORS[0]} stopOpacity={0.55} />
        <stop offset="65%" stopColor={TREND_CHART_SERIES_COLORS[0]} stopOpacity={0.3} />
        <stop offset="172%" stopColor={TREND_CHART_SERIES_COLORS[0]} stopOpacity={0.0} />
      </linearGradient>
    </defs>
  );
}

export function TrendChart({
  xAxisData,
  xAxisTickFormatter,
  xAxisValueFormatter,
  series,
}: TrendChartProps): ReactNode {
  const theme = useTheme();
  const axisTickStyle = { fill: theme.palette.text.secondary, fontSize: 11, fontWeight: 500 };
  const axisLabelStyle = { fontSize: 12, fontWeight: 700 };
  const formatXAxisValue = xAxisValueFormatter ?? xAxisTickFormatter;

  const formatOrDash =
    (format: (value: number) => string) =>
    (value: number | null): string =>
      value == null ? '-' : format(value);

  const seriesColor = (metric: TrendChartSeries, index: number): string =>
    metric.color ?? TREND_CHART_SERIES_COLORS[index] ?? theme.palette.text.disabled;

  const resolvedYAxisIds = series.map((metric) => metric.yAxisId ?? metric.id);
  const uniqueYAxisIds = [...new Set(resolvedYAxisIds)].slice(0, 2);

  return (
    <LineChart
      height={280}
      margin={{ top: 30, bottom: 10, left: 8, right: 8 }}
      grid={{ horizontal: true }}
      axisHighlight={{ x: 'line' }}
      xAxis={[
        {
          data: xAxisData,
          scaleType: 'point',
          height: 50,
          valueFormatter: (value: string | number, context) =>
            context.location === 'tick' ? xAxisTickFormatter(value) : formatXAxisValue(value),
          tickLabelStyle: { ...axisTickStyle, angle: -25, textAnchor: 'end' },
        },
      ]}
      yAxis={uniqueYAxisIds.map((yAxisId, axisIndex) => {
        const firstSeriesIndex = resolvedYAxisIds.indexOf(yAxisId);
        const firstSeries = series[firstSeriesIndex];
        return {
          id: yAxisId,
          position: axisIndex === 0 ? 'left' : 'right',
          label: firstSeries?.label ?? '',
          valueFormatter: firstSeries?.valueFormatter ?? ((value: number) => String(value)),
          width: axisIndex === 0 ? 70 : 64,
          tickLabelStyle: axisTickStyle,
          labelStyle: {
            ...axisLabelStyle,
            fill:
              firstSeries === undefined
                ? theme.palette.text.disabled
                : seriesColor(firstSeries, firstSeriesIndex),
          },
        };
      })}
      series={series.map((metric, index) => ({
        id: metric.id,
        data: metric.values,
        label: metric.label,
        yAxisId: metric.yAxisId ?? metric.id,
        color: seriesColor(metric, index),
        curve: 'linear',
        area: index === 0,
        showMark: false,
        valueFormatter: formatOrDash(metric.valueFormatter),
      }))}
      hideLegend
      sx={[
        trendChartSx(theme),
        ...series
          .filter((metric) => metric.dashed === true)
          .map((metric) => ({
            [`& .MuiLineChart-line[data-series="${metric.id}"]`]: { strokeDasharray: '6 4' },
          })),
      ]}
    >
      <TrendChartAreaGradient />
    </LineChart>
  );
}
